import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  ProgressBar,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { io } from "socket.io-client";
import {
  transformCSVs,
  transformQB,
  confirmPreview,
  downloadFile,
  getMigrationPractices,
  abortMigration,
} from "../dataMigrationApi";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

// When Azure Web PubSub is configured the Socket.IO connection must terminate
// at the PubSub endpoint, not at the backend's /socket.io. Connecting to the
// backend directly establishes a *local* Socket.IO connection that joins rooms
// locally, but io.to(room).emit() is hijacked by the useAzureSocketIO adapter
// and routed via Web PubSub — which has no record of the local socket. Result:
// emits go nowhere and the UI hangs on "Waiting for progress…". This mirrors
// the SOCKET_URL/SOCKET_PATH pair in BootNav.jsx, which is the canonical wiring.
const SOCKET_URL =
  process.env.REACT_APP_PUBSUB_PROD_URL ||
  process.env.REACT_APP_PUBSUB_URL ||
  URL;
const SOCKET_PATH = (process.env.REACT_APP_PUBSUB_PROD_URL || process.env.REACT_APP_PUBSUB_URL)
  ? `/clients/socketio/hubs/${process.env.REACT_APP_PUBSUB_HUB || "Hub"}`
  : "/socket.io";

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  const steps = ["Upload CSVs", "Processing", "Results"];
  return (
    <div className="d-flex justify-content-center mb-4">
      {steps.map((label, idx) => {
        const num = idx + 1;
        const isActive = num === step;
        const isDone = num < step;
        return (
          <React.Fragment key={num}>
            <div className="d-flex flex-column align-items-center" style={{ minWidth: 90 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: isDone ? "#198754" : isActive ? "#0d6efd" : "#dee2e6",
                  color: isDone || isActive ? "#fff" : "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                {isDone ? "✓" : num}
              </div>
              <small
                className="mt-1 text-center"
                style={{
                  color: isActive ? "#0d6efd" : isDone ? "#198754" : "#6c757d",
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 12,
                }}
              >
                {label}
              </small>
            </div>
            {idx < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: isDone ? "#198754" : "#dee2e6",
                  alignSelf: "flex-start",
                  marginTop: 17,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── DocFieldsTable — renders account or patient document fields ──────────────

const INTERNAL_FIELDS = new Set([
  "_id", "accountDependantMap", "accountBeneficiaryKeys",
  "accountPatientId", "accountPractice", "patientPractice",
]);

function renderFieldValue(v) {
  if (v === null || v === undefined || v === "") return <span className="text-muted">—</span>;
  if (typeof v === "object" && v.$oid) return <span>{v.$oid}</span>;
  if (typeof v === "object" && v.$date) return <span>{v.$date}</span>;
  if (Array.isArray(v)) return <span>{v.join(", ") || "—"}</span>;
  if (typeof v === "object") return <span className="text-muted fst-italic">{JSON.stringify(v)}</span>;
  return <span>{String(v)}</span>;
}

function DocFieldsTable({ doc }) {
  if (!doc) return <p className="text-muted small mb-0">No document</p>;
  const entries = Object.entries(doc).filter(([k, v]) => {
    if (INTERNAL_FIELDS.has(k)) return false;
    if (v === null || v === undefined || v === "") return false;
    return true;
  });
  if (!entries.length) return <p className="text-muted small mb-0">No fields</p>;
  return (
    <table className="table table-sm table-bordered mb-0" style={{ fontSize: 11 }}>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="text-muted font-monospace py-1 px-2" style={{ width: "42%", whiteSpace: "nowrap" }}>{k}</td>
            <td className="font-monospace py-1 px-2" style={{ wordBreak: "break-all" }}>{renderFieldValue(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function DataMigration() {
  const navigate = useNavigate();

  // Role guard
  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) { navigate("/login"); return; }
    try {
      const decoded = jwtDecode(token);
      const roles = []
        .concat(decoded?.role || [])
        .concat(decoded?.roles || [])
        .concat(decoded?.permissions || []);
      if (!roles.some((r) => ["admin", "owner", "SuperAdmin"].includes(r))) {
        navigate("/home");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch practices on mount
  useEffect(() => {
    getMigrationPractices()
      .then((list) => {
        setPractices(list);
        if (list.length === 1) setSelectedPractice(list[0]);
      })
      .catch(() => setPractices([]))
      .finally(() => setPracticesLoading(false));
  }, []);

  // Migration type
  const [migType, setMigType] = useState("panacea"); // "panacea" | "qb"

  // Step state
  const [step, setStep] = useState(1);

  // Step 1
  const [patientsFile, setPatientsFile] = useState(null);
  const [accountsFile, setAccountsFile] = useState(null);
  const [qbFile, setQbFile] = useState(null);

  // Preview (QB only)
  const [awaitingPreview, setAwaitingPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);

  // Step 2 — Processing (CSV parse + MedPrax + family checks + enrich)
  const [enrichProgress, setEnrichProgress] = useState({ phase: 0, label: "", current: 0, total: 0 });
  const [enrichLog, setEnrichLog] = useState([]);
  const [enrichDone, setEnrichDone] = useState(false);
  const [enrichError, setEnrichError] = useState("");
  const [familyCheckResults, setFamilyCheckResults] = useState([]);
  const [aborting, setAborting] = useState(false);
  const socketRef = useRef(null);
  const logEndRef = useRef(null);

  // Step 3
  const [enrichSummary, setEnrichSummary] = useState(null);
  const [downloadError, setDownloadError] = useState("");

  // Warn before leaving during processing
  useEffect(() => {
    if (step !== 2) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [step]);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [enrichLog]);

  // Cleanup socket on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }
    };
  }, []);

  // ── Step 1: upload CSVs → kick off full async pipeline ──────────────────────

  function validateFiles() {
    if (!selectedPractice) return "Please select the practice for this migration.";
    if (migType === "qb") {
      if (!qbFile) return "Please select the QuickBooks xlsx file.";
      if (!qbFile.name.toLowerCase().endsWith(".xlsx")) return "File must be a .xlsx file.";
    } else {
      if (!patientsFile) return "Please select the patients CSV file.";
      if (!accountsFile) return "Please select the accounts CSV file.";
      if (!patientsFile.name.toLowerCase().endsWith(".csv")) return "Patients file must be a .csv file.";
      if (!accountsFile.name.toLowerCase().endsWith(".csv")) return "Accounts file must be a .csv file.";
    }
    return "";
  }

  const handleTransform = useCallback(async () => {
    const err = validateFiles();
    if (err) { setUploadError(err); return; }
    setUploadError("");
    setUploading(true);
    setEnrichLog([]);
    setEnrichDone(false);
    setEnrichError("");
    setFamilyCheckResults([]);
    setEnrichProgress({ phase: 0, label: "Starting…", current: 0, total: 0 });

    // Wire socket BEFORE posting so we never miss the first progress event
    const token = sessionStorage.getItem("user");
    let userId;
    try { userId = jwtDecode(token)._id; } catch { userId = null; }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }
    const socket = io(SOCKET_URL, {
      path: SOCKET_PATH,     // route through Web PubSub when configured — matches BootNav
      auth: { token },       // JWT for Phase 26 handshake middleware
      query: { userId },     // local-dev bypass fallback
      transports: ["websocket", "polling"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("datamigration:progress", (data) => {
      setEnrichProgress(data);
      if (data.checkResult) {
        setFamilyCheckResults((prev) => [...prev, data.checkResult]);
      }
      setEnrichLog((prev) => {
        const msg = `[Phase ${data.phase}] ${data.label} (${data.current}/${data.total})`;
        return [...prev, msg].slice(-20);
      });
    });

    socket.on("datamigration:done", (data) => {
      setEnrichDone(true);
      setAborting(false);
      setEnrichSummary(data.summary);
      socket.removeAllListeners();
      socket.disconnect();
      setTimeout(() => setStep(3), 1200);
    });

    socket.on("datamigration:error", (data) => {
      setEnrichError(data.message || "Pipeline failed");
      setEnrichDone(true);
      setAborting(false);
    });

    socket.on("datamigration:aborted", () => {
      setEnrichError("Migration was aborted.");
      setEnrichDone(true);
      setAborting(false);
      setAwaitingPreview(false);
    });

    socket.on("datamigration:preview", (data) => {
      setPreviewData(data);
      setAwaitingPreview(true);
    });

    try {
      // Wait for the new socket to fully connect (server-side room join completes
      // on 'connection') before firing the POST. Without this, the backend can
      // start emitting datamigration:progress before this socket has joined
      // user:<id>, and every emit in that window is dropped — see commit log
      // for the Web PubSub disconnect/reconnect race that left users stuck on
      // "Waiting for progress…" indefinitely.
      if (!socket.connected) {
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => {
            socket.off("connect", onConnect);
            socket.off("connect_error", onError);
            reject(new Error("Socket connect timed out after 10s"));
          }, 10000);
          const onConnect = () => {
            clearTimeout(timer);
            socket.off("connect_error", onError);
            resolve();
          };
          const onError = (err) => {
            clearTimeout(timer);
            socket.off("connect", onConnect);
            reject(err);
          };
          socket.once("connect", onConnect);
          socket.once("connect_error", onError);
        });
      }

      if (migType === "qb") {
        await transformQB(qbFile, selectedPractice.practiceNumber);
      } else {
        await transformCSVs(patientsFile, accountsFile, selectedPractice.practiceNumber);
      }
      setStep(2);
    } catch (e) {
      setUploadError(e?.response?.data?.error || e.message || "Upload failed");
      socket.removeAllListeners();
      socket.disconnect();
    } finally {
      setUploading(false);
    }
  }, [migType, patientsFile, accountsFile, qbFile, selectedPractice]);

  async function handleAbort() {
    setAborting(true);
    try {
      await abortMigration();
    } catch {
      // Server will still set the flag; just wait for the aborted event
    }
  }

  // ── Step 4: downloads ────────────────────────────────────────────────────────

  async function handleDownload(type) {
    setDownloadError("");
    try {
      await downloadFile(type);
    } catch (e) {
      setDownloadError(e?.response?.data?.error || e.message || "Download failed");
    }
  }

  function handleReset() {
    setPatientsFile(null);
    setAccountsFile(null);
    setQbFile(null);
    if (practices.length !== 1) setSelectedPractice(null);
    setUploadError("");
    setEnrichProgress({ phase: 0, label: "", current: 0, total: 0 });
    setEnrichLog([]);
    setEnrichDone(false);
    setEnrichError("");
    setFamilyCheckResults([]);
    setAborting(false);
    setEnrichSummary(null);
    setDownloadError("");
    setAwaitingPreview(false);
    setPreviewData(null);
    setStep(1);
  }

  // ── Progress % for phase bar ─────────────────────────────────────────────────

  function phasePercent(phase, current, total) {
    const base = (phase - 1) * 25;
    const within = total > 0 ? (current / total) * 25 : 0;
    return Math.min(Math.round(base + within), 99);
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Container fluid className="py-4" style={{ maxWidth: 900 }}>
      <h4 className="mb-1 fw-bold mt-5">Data Migration</h4>
      <p className="text-muted mb-4" style={{ fontSize: 13 }}>
        Panacea CSV → PractoSync MongoDB import pipeline
      </p>

      <StepIndicator step={step} />

      {/* ── STEP 1: Upload Files ─────────────────────────────────────────────── */}
      {step === 1 && (
        <Card className="shadow-sm">
          <Card.Header className="fw-semibold">Step 1 — Upload Files</Card.Header>
          <Card.Body>
            {uploadError && <Alert variant="danger">{uploadError}</Alert>}

            {/* Migration type */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Migration Type</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check
                  type="radio"
                  id="mig-panacea"
                  label="Panacea Migration"
                  checked={migType === "panacea"}
                  onChange={() => { setMigType("panacea"); setUploadError(""); }}
                />
                <Form.Check
                  type="radio"
                  id="mig-qb"
                  label="QuickBooks Migration"
                  checked={migType === "qb"}
                  onChange={() => { setMigType("qb"); setUploadError(""); }}
                />
              </div>
            </Form.Group>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Practice</Form.Label>
                  {practicesLoading ? (
                    <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 13 }}>
                      <Spinner animation="border" size="sm" />
                      Loading practices…
                    </div>
                  ) : practices.length === 0 ? (
                    <Alert variant="warning" className="mb-0 py-2">
                      No practices found for your account. Contact your administrator.
                    </Alert>
                  ) : (
                    <Form.Select
                      value={selectedPractice?.practiceNumber || ""}
                      onChange={(e) => {
                        const prac = practices.find((p) => p.practiceNumber === e.target.value) || null;
                        setSelectedPractice(prac);
                        setUploadError("");
                      }}
                    >
                      <option value="">— Select practice —</option>
                      {practices.map((p) => (
                        <option key={p.practiceNumber} value={p.practiceNumber}>
                          {p.practiceName || p.practiceNumber} ({p.practiceNumber})
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {migType === "panacea" ? (
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label className="fw-semibold">patients.csv</Form.Label>
                    <Form.Control type="file" accept=".csv" onChange={(e) => { setPatientsFile(e.target.files[0] || null); setUploadError(""); }} />
                    <Form.Text className="text-muted">Export from Panacea: Patients list</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label className="fw-semibold">accounts.csv</Form.Label>
                    <Form.Control type="file" accept=".csv" onChange={(e) => { setAccountsFile(e.target.files[0] || null); setUploadError(""); }} />
                    <Form.Text className="text-muted">Export from Panacea: Account holders</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label className="fw-semibold">QuickBooks export (.xlsx)</Form.Label>
                    <Form.Control type="file" accept=".xlsx" onChange={(e) => { setQbFile(e.target.files[0] || null); setUploadError(""); }} />
                    <Form.Text className="text-muted">Single Excel file with account and patient data</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <div className="d-flex justify-content-end mt-2">
              <Button
                variant="primary"
                onClick={handleTransform}
                disabled={uploading || !selectedPractice || (migType === "qb" ? !qbFile : (!patientsFile || !accountsFile))}
              >
                {uploading ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Transforming…</>
                ) : (
                  "Upload & Transform"
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── STEP 2: Processing in progress ───────────────────────────────────── */}
      {step === 2 && (
        <Card className="shadow-sm">
          <Card.Header className="fw-semibold">Step 2 — Processing</Card.Header>
          <Card.Body>
            {enrichError && <Alert variant="danger">{enrichError}</Alert>}

            {/* Phase badges */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {[
                { n: 1, label: "Parse CSVs" },
                { n: 2, label: "MedPrax Lookup" },
                { n: 3, label: "Family Checks" },
                { n: 4, label: "Link & Enrich" },
              ].map(({ n, label }) => (
                <Badge
                  key={n}
                  bg={
                    enrichProgress.phase > n
                      ? "success"
                      : enrichProgress.phase === n
                      ? "primary"
                      : "secondary"
                  }
                  className="fs-6 px-3 py-2"
                >
                  {n}. {label}
                  {enrichProgress.phase === n && !enrichDone && (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  )}
                </Badge>
              ))}
            </div>

            {/* Progress bar */}
            <ProgressBar
              now={enrichDone ? 100 : phasePercent(enrichProgress.phase, enrichProgress.current, enrichProgress.total)}
              label={
                enrichDone
                  ? "Complete"
                  : enrichProgress.total > 0
                  ? `${enrichProgress.current} / ${enrichProgress.total}`
                  : ""
              }
              animated={!enrichDone}
              striped={!enrichDone}
              variant={enrichDone ? "success" : "primary"}
              className="mb-3"
              style={{ height: 24, fontSize: 13 }}
            />

            {enrichProgress.label && (
              <p className="text-muted small mb-2">{enrichProgress.label}</p>
            )}

            {/* Family check live results */}
            {familyCheckResults.length > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="fw-semibold text-muted">Family Checks</small>
                  <small>
                    <span className="text-success fw-semibold">
                      ✓ {familyCheckResults.filter((r) => r.ok).length} approved
                    </span>
                    <span className="text-muted mx-1">·</span>
                    <span className="text-danger fw-semibold">
                      ✗ {familyCheckResults.filter((r) => !r.ok).length} rejected
                    </span>
                  </small>
                </div>
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                >
                  {familyCheckResults.map((r, i) => (
                    <div
                      key={i}
                      className="d-flex align-items-center px-2 py-1"
                      style={{
                        borderBottom: i < familyCheckResults.length - 1 ? "1px solid #f0f0f0" : "none",
                        background: r.ok ? "#f6fff8" : "#fff8f8",
                      }}
                    >
                      <span
                        className="fw-bold me-2"
                        style={{ color: r.ok ? "#198754" : "#dc3545", minWidth: 14 }}
                      >
                        {r.ok ? "✓" : "✗"}
                      </span>
                      <span className="font-monospace flex-grow-1">{r.memNo}</span>
                      {!r.ok && (
                        <Badge bg="danger" style={{ fontSize: 10 }}>
                          {r.detail || r.code}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Preview panel ─────────────────────────────────────────────── */}
            {awaitingPreview && previewData && (
              <Card className="border-warning mb-3">
                <Card.Header className="bg-warning bg-opacity-25 fw-semibold">
                  Preview — First {previewData.records?.length} Records
                  <small className="text-muted ms-2 fw-normal">
                    ({previewData.totalRecords} total · {previewData.medAidCount} medical aid · {previewData.privateCount} private)
                  </small>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted small mb-3">
                    Verify the exact fields and values that will be saved to MongoDB. Expand each record to inspect.
                  </p>
                  <Accordion>
                    {(previewData.records || []).map((r, i) => (
                      <Accordion.Item key={i} eventKey={String(i)}>
                        <Accordion.Header>
                          <span className="font-monospace me-2" style={{ fontSize: 12 }}>#{r.accountNumber}</span>
                          <span className="me-3" style={{ fontSize: 13 }}>
                            {r.accountDoc?.accountFirstName} {r.accountDoc?.accountLastName}
                          </span>
                          {r.familyCheck?.ok
                            ? <Badge bg="success" className="me-1">✓ FC: {r.familyCheck.detail}</Badge>
                            : <Badge bg="danger" className="me-1">✗ {r.familyCheck?.detail}</Badge>
                          }
                        </Accordion.Header>
                        <Accordion.Body className="p-2">
                          <Row className="g-2">
                            <Col md={6}>
                              <div className="fw-semibold mb-1" style={{ fontSize: 12 }}>Account document</div>
                              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                                <DocFieldsTable doc={r.accountDoc} />
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="fw-semibold mb-1" style={{ fontSize: 12 }}>Patient document</div>
                              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                                <DocFieldsTable doc={r.patientDoc} />
                              </div>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Card.Body>
                <Card.Footer className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={async () => { setAborting(true); await handleAbort(); }}
                    disabled={aborting}
                  >
                    Abort
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={async () => {
                      setAwaitingPreview(false);
                      await confirmPreview();
                    }}
                  >
                    Looks correct — continue import
                  </Button>
                </Card.Footer>
              </Card>
            )}

            {/* Live log */}
            <div
              style={{
                background: "#1e1e1e",
                color: "#d4d4d4",
                fontFamily: "monospace",
                fontSize: 12,
                padding: "10px 14px",
                borderRadius: 6,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {enrichLog.length === 0 && (
                <span style={{ opacity: 0.5 }}>Waiting for progress…</span>
              )}
              {enrichLog.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
              <div ref={logEndRef} />
            </div>

            {!enrichDone && (
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleAbort}
                  disabled={aborting}
                >
                  {aborting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Aborting…
                    </>
                  ) : (
                    "✕ Abort Migration"
                  )}
                </Button>
              </div>
            )}

            {enrichDone && !enrichError && (
              <Alert variant="success" className="mt-3 mb-0">
                Processing complete. Advancing to results…
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ── STEP 3: Results & Downloads ──────────────────────────────────────── */}
      {step === 3 && enrichSummary && (
        <Card className="shadow-sm">
          <Card.Header className="fw-semibold">Step 3 — Results & Downloads</Card.Header>
          <Card.Body>
            {downloadError && <Alert variant="danger">{downloadError}</Alert>}

            {/* ── Import-ready counts ── */}
            <Row className="g-3 mb-3">
              <Col xs={6} md={3}>
                <Card className="text-center border-success">
                  <Card.Body className="py-3">
                    <div className="fs-2 fw-bold text-success">{enrichSummary.enrichedPatientsOut}</div>
                    <div className="text-muted small">Patients ready</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center border-primary">
                  <Card.Body className="py-3">
                    <div className="fs-2 fw-bold text-primary">{enrichSummary.accountsEnriched}</div>
                    <div className="text-muted small">Accounts enriched</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center border-danger">
                  <Card.Body className="py-3">
                    <div className="fs-2 fw-bold text-danger">{enrichSummary.accountsFailed}</div>
                    <div className="text-muted small">Failed family checks</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center border-warning">
                  <Card.Body className="py-3">
                    <div className="fs-2 fw-bold text-warning">{enrichSummary.patientsExcluded}</div>
                    <div className="text-muted small">Patients excluded</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ── Exception counts ── */}
            <Row className="g-2 mb-4">
              {enrichSummary.unlinkedCount > 0 && (
                <Col xs={6} md={3}>
                  <Card className="text-center border-warning bg-warning bg-opacity-10">
                    <Card.Body className="py-2">
                      <div className="fs-4 fw-bold text-warning">{enrichSummary.unlinkedCount}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>Unlinked patients</div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
              {enrichSummary.multiAccountCount > 0 && (
                <Col xs={6} md={3}>
                  <Card className="text-center border-danger bg-danger bg-opacity-10">
                    <Card.Body className="py-2">
                      <div className="fs-4 fw-bold text-danger">{enrichSummary.multiAccountCount}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>Multi-account (POPIA)</div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
              {enrichSummary.skippedCount > 0 && (
                <Col xs={6} md={3}>
                  <Card className="text-center border-secondary bg-secondary bg-opacity-10">
                    <Card.Body className="py-2">
                      <div className="fs-4 fw-bold text-secondary">{enrichSummary.skippedCount}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>Skipped rows</div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>

            {/* ── Downloads ── */}
            <div className="d-flex gap-3 flex-wrap mb-4">
              <Button variant="success" onClick={() => handleDownload("enriched_patients")}>
                ↓ enriched_patients.json
              </Button>
              <Button variant="primary" onClick={() => handleDownload("enriched_accounts")}>
                ↓ enriched_accounts.json
              </Button>
              <Button variant="warning" onClick={() => handleDownload("exceptions_report")}>
                ↓ exceptions_report.json
              </Button>
            </div>

            <div className="d-flex justify-content-end">
              <Button variant="outline-danger" onClick={handleReset}>
                ↺ Start New Migration
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
